import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Hook to fetch and check user permissions
 * 
 * Usage:
 * const { hasPermission, isAdmin, loading } = usePermissions();
 * 
 * if (hasPermission('orders.assign_driver')) { ... }
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if user is admin (either is_admin flag or 'admin' in roles)
  const isAdmin = user?.is_admin || user?.roles?.includes('admin');

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      // Admin has all permissions
      if (isAdmin) {
        setPermissions(['all']);
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/users/${user.id}/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPermissions(response.data.permissions || []);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [user?.id, isAdmin]);

  const hasPermission = (permission) => {
    if (isAdmin || permissions.includes('all')) return true;
    return permissions.includes(permission);
  };

  return { 
    permissions, 
    hasPermission, 
    isAdmin,
    loading 
  };
};

export default usePermissions;
